<template>
  <ui-combobox-select
    v-if="searchable"
    :model-value="selectedOption === null ? modelValue : valueOf(selectedOption)"
    :options="options"
    :option-value="valueOf"
    :option-label="labelOf"
    :option-disabled="(option) => !selectable(option)"
    :placeholder="loading ? tr('Loading...') : placeholder"
    :search-placeholder="tr('Search')"
    :empty-text="tr('No results')"
    :disabled="disabled || loading"
    :class="triggerClass"
    @update:model-value="(value) => $emit('update:modelValue', value)"
  >
    <template v-if="$slots.option" #option="{ option }">
      <slot name="option" :option="option" />
    </template>
  </ui-combobox-select>
  <ui-select
    v-else
    :model-value="selectedKey"
    :disabled="disabled || loading"
    @update:model-value="(key) => onSelect(key)"
  >
    <ui-select-trigger :class="triggerClass" :aria-label="ariaLabel || placeholder || null">
      <span v-if="loading" class="flex items-center gap-2 text-muted-foreground">
        <loader-circle-icon class="size-4 animate-spin" />
        {{ tr('Loading...') }}
      </span>
      <span v-else-if="selectedOption !== null && $slots.option" class="flex min-w-0 items-center gap-2 overflow-x-clip whitespace-nowrap">
        <slot name="option" :option="selectedOption" />
      </span>
      <ui-select-value v-else :placeholder="placeholder" />
    </ui-select-trigger>
    <ui-select-content>
      <ui-select-item
        v-for="(option, index) in options"
        :key="index"
        :value="String(index)"
        :disabled="!selectable(option)"
      >
        <slot name="option" :option="option">{{ labelOf(option) }}</slot>
      </ui-select-item>
    </ui-select-content>
  </ui-select>
</template>

<script>
import { LoaderCircle } from '@lucide/vue';

/**
 * Single-choice select with the vue-select props the forms use: `options`,
 * `reduce` (option → value), `label` (key or function for the text),
 * `selectable`, `loading`, `searchable` (combobox) and an `option` slot.
 * A `label` key is translated; a `label` function returns the final text.
 * Object values match by `id` when both have one (like vue-select).
 */
export default {
  name: 'SelectField',
  components: {
    LoaderCircleIcon: LoaderCircle,
  },
  props: {
    modelValue: {
      type: null,
      required: false,
      default: () => null,
    },
    options: {
      type: Array,
      required: false,
      default: () => [],
    },
    reduce: {
      type: Function,
      required: false,
      default: option => option,
    },
    label: {
      type: [ String, Function ],
      required: false,
      default: () => 'label',
    },
    placeholder: {
      type: String,
      required: false,
      default: () => '',
    },
    triggerClass: {
      type: String,
      required: false,
      default: () => 'w-full',
    },
    ariaLabel: {
      type: String,
      required: false,
      default: () => '',
    },
    searchable: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    selectable: {
      type: Function,
      required: false,
      default: () => true,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    loading: {
      type: Boolean,
      required: false,
      default: () => false,
    },
  },
  emits: [ 'update:modelValue' ],
  computed: {
    selectedIndex() {
      return this.options.findIndex(option => this.isSameValue(this.valueOf(option), this.modelValue));
    },
    selectedKey() {
      return this.selectedIndex === -1 ? undefined : String(this.selectedIndex);
    },
    selectedOption() {
      return this.selectedIndex === -1 ? null : this.options[this.selectedIndex];
    },
  },
  methods: {
    isSameValue(a, b) {
      if (a && b && typeof a === 'object' && typeof b === 'object' && 'id' in a && 'id' in b) {
        return a.id === b.id;
      }

      return a === b;
    },
    valueOf(option) {
      return this.reduce(option);
    },
    labelOf(option) {
      if (typeof this.label === 'function') {
        return this.label(option);
      }
      // Object labels are translation keys (as the old global vue-select config did).
      if (option !== null && typeof option === 'object') {
        return this.tr(String(option[this.label] ?? ''));
      }

      return String(option ?? '');
    },
    onSelect(key) {
      const option = this.options[Number(key)];
      if (option !== undefined) {
        this.$emit('update:modelValue', this.valueOf(option));
      }
    },
  },
};
</script>
